import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bell, Fingerprint, MapPin, Plus, Hand, ScanFace, Watch, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/** SettingsAttendanceTab — extracted verbatim from SettingsPage.jsx (iter792). */
export function SettingsAttendanceTab({ attSettings, setAttSettings, alertSettings, setAlertSettings, branches }) {
  return (
    <>
            <div className="space-y-6">
              <Card data-testid="attendance-methods-card">
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center gap-2">
                    <Fingerprint size={18} className="text-info" />
                    Attendance Methods
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">Control which methods employees can use to clock in/out</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                      { key: 'manual', label: 'Manual', desc: 'Click button to clock in/out', Icon: Hand, color: 'text-gray-500' },
                      { key: 'fingerprint', label: 'Fingerprint', desc: 'Biometric fingerprint scan', Icon: Fingerprint, color: 'text-info' },
                      { key: 'face', label: 'Face Recognition', desc: 'Camera-based face ID', Icon: ScanFace, color: 'text-violet-500' },
                      { key: 'watch', label: 'Smart Watch', desc: 'Wearable device check-in', Icon: Watch, color: 'text-success' },
                    ].map(m => {
                      const isEnabled = attSettings.allowed_methods?.includes(m.key);
                      return (
                        <button
                          key={m.key}
                          onClick={() => {
                            const methods = isEnabled
                              ? attSettings.allowed_methods.filter(x => x !== m.key)
                              : [...(attSettings.allowed_methods || []), m.key];
                            if (methods.length === 0) { toast.error('At least one method required'); return; }
                            setAttSettings(prev => ({ ...prev, allowed_methods: methods }));
                          }}
                          className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                            isEnabled
                              ? 'border-blue-400 bg-info/10 dark:bg-blue-950 dark:border-info'
                              : 'border-gray-200 bg-white dark:bg-muted dark:border-stone-700 opacity-50'
                          }`}
                          data-testid={`att-method-${m.key}`}
                        >
                          <m.Icon size={28} className={isEnabled ? m.color : 'text-gray-400'} />
                          <p className="text-sm font-semibold dark:text-white">{m.label}</p>
                          <p className="text-[10px] text-muted-foreground text-center">{m.desc}</p>
                          <Badge variant={isEnabled ? 'default' : 'secondary'} className="text-[10px]">
                            {isEnabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Geofencing */}
              <Card data-testid="geofence-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-outfit flex items-center gap-2">
                        <MapPin size={18} className="text-error" />
                        Geofencing
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">Restrict clock-in/out to specific office locations</p>
                    </div>
                    <Switch
                      checked={attSettings.geofence?.enabled || false}
                      onCheckedChange={(v) => setAttSettings(prev => ({ ...prev, geofence: { ...prev.geofence, enabled: v } }))}
                      data-testid="geofence-toggle"
                    />
                  </div>
                </CardHeader>
                {attSettings.geofence?.enabled && (
                  <CardContent className="space-y-4">
                    <div className="p-3 bg-warning/10 border border-warning/30 rounded-xl text-xs text-warning dark:bg-amber-900/10 dark:border-amber-800 dark:text-amber-400">
                      When enabled, employees must share their GPS location when clocking in/out. They will be blocked if outside the configured radius.
                    </div>

                    {/* Location list */}
                    <div className="space-y-3">
                      {(attSettings.geofence?.locations || []).map((loc, idx) => (
                        <div key={loc.id || idx} className="p-4 border rounded-xl dark:border-stone-700 bg-muted/50 dark:bg-muted" data-testid={`geofence-loc-${idx}`}>
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex items-center gap-2">
                              <MapPin size={16} className="text-error" />
                              <span className="text-sm font-semibold dark:text-white">{loc.name || `Location ${idx + 1}`}</span>
                            </div>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-error hover:bg-error/10" onClick={() => {
                              setAttSettings(prev => ({
                                ...prev,
                                geofence: { ...prev.geofence, locations: prev.geofence.locations.filter((_, i) => i !== idx) }
                              }));
                            }} data-testid={`remove-geofence-loc-${idx}`}>
                              <X size={14} />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                              <Label className="text-xs">Name</Label>
                              <Input value={loc.name || ''} placeholder="Main Office" className="h-8 mt-1 text-xs" data-testid={`geofence-name-${idx}`}
                                onChange={(e) => {
                                  const locs = [...attSettings.geofence.locations];
                                  locs[idx] = { ...locs[idx], name: e.target.value };
                                  setAttSettings(prev => ({ ...prev, geofence: { ...prev.geofence, locations: locs } }));
                                }} />
                            </div>
                            <div>
                              <Label className="text-xs">Latitude</Label>
                              <Input type="number" step="any" value={loc.latitude ?? ''} placeholder="24.7136" className="h-8 mt-1 text-xs font-mono" data-testid={`geofence-lat-${idx}`}
                                onChange={(e) => {
                                  const locs = [...attSettings.geofence.locations];
                                  locs[idx] = { ...locs[idx], latitude: parseFloat(e.target.value) || 0 };
                                  setAttSettings(prev => ({ ...prev, geofence: { ...prev.geofence, locations: locs } }));
                                }} />
                            </div>
                            <div>
                              <Label className="text-xs">Longitude</Label>
                              <Input type="number" step="any" value={loc.longitude ?? ''} placeholder="46.6753" className="h-8 mt-1 text-xs font-mono" data-testid={`geofence-lng-${idx}`}
                                onChange={(e) => {
                                  const locs = [...attSettings.geofence.locations];
                                  locs[idx] = { ...locs[idx], longitude: parseFloat(e.target.value) || 0 };
                                  setAttSettings(prev => ({ ...prev, geofence: { ...prev.geofence, locations: locs } }));
                                }} />
                            </div>
                            <div>
                              <Label className="text-xs">Radius (m)</Label>
                              <Select value={String(loc.radius || 200)} onValueChange={(v) => {
                                const locs = [...attSettings.geofence.locations];
                                locs[idx] = { ...locs[idx], radius: parseInt(v) };
                                setAttSettings(prev => ({ ...prev, geofence: { ...prev.geofence, locations: locs } }));
                              }}>
                                <SelectTrigger className="h-8 mt-1 text-xs" data-testid={`geofence-radius-${idx}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="50">50m</SelectItem>
                                  <SelectItem value="100">100m</SelectItem>
                                  <SelectItem value="200">200m (Default)</SelectItem>
                                  <SelectItem value="500">500m</SelectItem>
                                  <SelectItem value="1000">1km</SelectItem>
                                  <SelectItem value="2000">2km</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {branches.length > 0 && (
                            <div className="mt-3">
                              <Label className="text-xs">Branch (Optional)</Label>
                              <Select value={loc.branch_id || '__all__'} onValueChange={(v) => {
                                const locs = [...attSettings.geofence.locations];
                                locs[idx] = { ...locs[idx], branch_id: v === '__all__' ? null : v };
                                setAttSettings(prev => ({ ...prev, geofence: { ...prev.geofence, locations: locs } }));
                              }}>
                                <SelectTrigger className="h-8 mt-1 text-xs w-60" data-testid={`geofence-branch-${idx}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__all__">All Branches</SelectItem>
                                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <Button variant="outline" size="sm" className="mt-3 text-xs h-7 rounded-lg" data-testid={`use-location-btn-${idx}`}
                            onClick={() => {
                              if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
                              toast.info('Getting your location...');
                              navigator.geolocation.getCurrentPosition((pos) => {
                                const locs = [...attSettings.geofence.locations];
                                locs[idx] = { ...locs[idx], latitude: parseFloat(pos.coords.latitude.toFixed(6)), longitude: parseFloat(pos.coords.longitude.toFixed(6)) };
                                setAttSettings(prev => ({ ...prev, geofence: { ...prev.geofence, locations: locs } }));
                                toast.success(`Location set: ${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`);
                              }, () => toast.error('Failed to get location. Check GPS permissions.'));
                            }}>
                            <MapPin size={12} className="mr-1" /> Use Current Location
                          </Button>
                        </div>
                      ))}
                    </div>

                    <Button variant="outline" className="w-full border-dashed" data-testid="add-geofence-location-btn"
                      onClick={() => {
                        setAttSettings(prev => ({
                          ...prev,
                          geofence: {
                            ...prev.geofence,
                            locations: [...(prev.geofence?.locations || []), { id: crypto.randomUUID?.() || Date.now().toString(), name: '', latitude: null, longitude: null, radius: 200, branch_id: null }]
                          }
                        }));
                      }}>
                      <Plus size={14} className="mr-2" /> Add Office Location
                    </Button>
                  </CardContent>
                )}
              </Card>

              {/* Automated Late/Absence Alerts */}
              <Card data-testid="alert-rules-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-outfit flex items-center gap-2">
                        <Bell size={18} className="text-warning" />
                        Automated Alerts
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">Get notified when employees are late or absent</p>
                    </div>
                    <Switch
                      checked={alertSettings.enabled}
                      onCheckedChange={(v) => setAlertSettings(prev => ({ ...prev, enabled: v }))}
                      data-testid="alert-toggle"
                    />
                  </div>
                </CardHeader>
                {alertSettings.enabled && (
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">Late Threshold (minutes)</Label>
                        <p className="text-[10px] text-muted-foreground mb-1">Alert if employee is late by this many minutes</p>
                        <Input
                          type="number" min={1} max={120}
                          value={alertSettings.late_threshold_minutes}
                          onChange={(e) => setAlertSettings(prev => ({ ...prev, late_threshold_minutes: parseInt(e.target.value) || 15 }))}
                          className="h-8 text-xs"
                          data-testid="alert-late-threshold"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Absence Check Time</Label>
                        <p className="text-[10px] text-muted-foreground mb-1">If not clocked in by this time, mark absent</p>
                        <Input
                          type="time"
                          value={alertSettings.absence_check_time}
                          onChange={(e) => setAlertSettings(prev => ({ ...prev, absence_check_time: e.target.value }))}
                          className="h-8 text-xs"
                          data-testid="alert-absence-time"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Consecutive Absence Days</Label>
                        <p className="text-[10px] text-muted-foreground mb-1">Critical alert after N consecutive absences</p>
                        <Input
                          type="number" min={1} max={30}
                          value={alertSettings.consecutive_absence_days}
                          onChange={(e) => setAlertSettings(prev => ({ ...prev, consecutive_absence_days: parseInt(e.target.value) || 3 }))}
                          className="h-8 text-xs"
                          data-testid="alert-consecutive-days"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs mb-2 block">Notify</Label>
                      <div className="flex gap-2">
                        {['admin', 'manager'].map(role => {
                          const active = alertSettings.notify_roles?.includes(role);
                          return (
                            <button
                              key={role}
                              onClick={() => {
                                const roles = active
                                  ? alertSettings.notify_roles.filter(r => r !== role)
                                  : [...(alertSettings.notify_roles || []), role];
                                if (roles.length === 0) { toast.error('At least one role required'); return; }
                                setAlertSettings(prev => ({ ...prev, notify_roles: roles }));
                              }}
                              className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${active ? 'border-amber-400 bg-warning/10 text-warning dark:bg-amber-900/20 dark:text-amber-400' : 'border-border text-muted-foreground dark:border-stone-700'}`}
                              data-testid={`alert-notify-${role}`}
                            >
                              {role.charAt(0).toUpperCase() + role.slice(1)}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={async () => {
                          try {
                            await api.put('/attendance/alert-settings', alertSettings);
                            toast.success('Alert rules saved');
                          } catch { toast.error('Failed to save alert rules'); }
                        }}
                        data-testid="save-alert-settings-btn"
                      >
                        Save Alert Rules
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>

              <div className="flex justify-end">
                <Button
                  onClick={async () => {
                    try {
                      await api.put('/attendance/settings', attSettings);
                      toast.success('Attendance settings saved');
                    } catch (err) { toast.error('Failed to save'); }
                  }}
                  className="bg-info hover:bg-blue-700"
                  data-testid="save-attendance-settings-btn"
                >
                  Save Attendance Settings
                </Button>
              </div>
            </div>
    </>
  );
}

export default SettingsAttendanceTab;
